import { useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAttachmentBlobUrl } from '../../lib/attachmentCache';
import { Button } from '../ui/button';
import api from '../../lib/api';
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Full-screen image preview with prev/next navigation + download.
 *
 * Props:
 *   - attachments: array of attachment objects (id, content_type,
 *     original_filename, size_bytes). Caller is expected to pass only
 *     image attachments — non-images are skipped.
 *   - index: currently shown index (controlled).
 *   - onIndexChange: bubble new index back.
 *   - onClose: close the lightbox.
 *
 * IMPORTANT — rendered via `createPortal` directly into `document.body`
 * AND keyboard listeners use capture phase + stopPropagation so the
 * lightbox can be safely opened ON TOP of a Radix Dialog/Sheet (e.g.
 * TaskViewDrawer). Without this:
 *   • Clicks on the arrows / X / Download fell through to the Sheet's
 *     outside-pointer scope, closing the parent drawer instead of
 *     navigating images.
 *   • ESC was processed by BOTH the lightbox AND Radix Dialog, so the
 *     drawer also closed.
 * Bug reported Feb 2026 — fixed by portalling + capture-phase listener.
 *
 * Closes on Escape, navigates with ←/→ arrows.
 */
export default function AttachmentLightbox({
  attachments = [],
  index = 0,
  onIndexChange,
  onClose,
}) {
  const images = useMemo(
    () => attachments.filter(a => (a.content_type || '').startsWith('image/')),
    [attachments]
  );
  const current = images[index];
  const url = useAttachmentBlobUrl(current?.id);

  const goPrev = useCallback(() => {
    if (images.length === 0) return;
    onIndexChange?.((index - 1 + images.length) % images.length);
  }, [images.length, index, onIndexChange]);

  const goNext = useCallback(() => {
    if (images.length === 0) return;
    onIndexChange?.((index + 1) % images.length);
  }, [images.length, index, onIndexChange]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        onClose?.();
      } else if (e.key === 'ArrowLeft') {
        e.stopPropagation();
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.stopPropagation();
        e.preventDefault();
        goNext();
      }
    };
    // Capture phase so we run BEFORE Radix Dialog's escape handler
    // (which would otherwise close the parent Sheet/Drawer).
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [onClose, goPrev, goNext]);

  const download = async () => {
    if (!current) return;
    try {
      const res = await api.get(`/tasks/attachments/${current.id}/download`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = current.original_filename || `attachment-${current.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Download failed');
    }
  };

  if (!current) return null;

  // Render directly into document.body so we sit OUTSIDE any parent
  // Radix Dialog/Sheet's pointer-event scope. Without the portal the
  // arrow/close/download buttons couldn't receive clicks because the
  // Sheet's outside-pointer guard intercepted them first.
  const body = (
    <div
      className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex items-center justify-center"
      style={{ pointerEvents: 'auto' }}
      onClick={onClose}
      data-testid="attachment-lightbox"
    >
      {/* Top-right controls */}
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <Button
          variant="ghost" size="icon"
          className="h-9 w-9 text-white/80 hover:text-white hover:bg-white/10"
          onClick={(e) => { e.stopPropagation(); download(); }}
          data-testid="attachment-lightbox-download"
          title="Download"
        >
          <Download className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost" size="icon"
          className="h-9 w-9 text-white/80 hover:text-white hover:bg-white/10"
          onClick={(e) => { e.stopPropagation(); onClose?.(); }}
          data-testid="attachment-lightbox-close"
          title="Close (Esc)"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Caption (filename + index) */}
      <div className="absolute top-4 left-4 text-xs text-white/70 max-w-[60%] truncate">
        {current.original_filename}
        <span className="ml-2 opacity-60">{index + 1} / {images.length}</span>
      </div>

      {/* Prev */}
      {images.length > 1 && (
        <Button
          variant="ghost" size="icon"
          className="absolute left-4 h-12 w-12 text-white/80 hover:text-white hover:bg-white/10 rounded-full"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          data-testid="attachment-lightbox-prev"
          title="Previous (←)"
        >
          <ChevronLeft className="h-7 w-7" />
        </Button>
      )}

      {/* Image */}
      <div className="max-w-[90vw] max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
        {url ? (
          <img
            src={url}
            alt={current.original_filename}
            className="max-w-full max-h-[85vh] object-contain rounded-md shadow-2xl"
            data-testid="attachment-lightbox-image"
          />
        ) : (
          <div className="text-white/60 text-sm">Loading…</div>
        )}
      </div>

      {/* Next */}
      {images.length > 1 && (
        <Button
          variant="ghost" size="icon"
          className="absolute right-4 h-12 w-12 text-white/80 hover:text-white hover:bg-white/10 rounded-full"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          data-testid="attachment-lightbox-next"
          title="Next (→)"
        >
          <ChevronRight className="h-7 w-7" />
        </Button>
      )}
    </div>
  );

  // `document` exists in the browser; SSR not a concern for this app.
  return createPortal(body, document.body);
}
